import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import type { Currency } from "../../data/BaseEntity";
import { Price } from "../../misc/price";
import type { InvoiceSellerId, SellerLimits } from "../ordersApi";
import { useOrderPriceByDepartment } from "./useOrderPriceByDepartment";

interface PriceTableProps {
  readonly sellerAssignmentOrder: InvoiceSellerId[];
  readonly advance: Partial<Record<InvoiceSellerId, SellerLimits>>;
  readonly proforma: Partial<Record<InvoiceSellerId, SellerLimits>>;
  readonly currency: Currency;
}

const PriceTable: FC<PriceTableProps> = ({ sellerAssignmentOrder, advance, proforma, currency }) => {
  const presentSellers = sellerAssignmentOrder.filter((id) => advance[id] || proforma[id]);

  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Sprzedawca</TableCell>
            <TableCell align="right">Wartość usługi</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {presentSellers.map((sellerId) => {
            const advanceLimits = advance[sellerId];
            const proformaLimits = proforma[sellerId];
            return (
              <TableRow key={sellerId}>
                <TableCell>{advanceLimits?.seller.shortName ?? proformaLimits!.seller.shortName}</TableCell>
                <TableCell align="right">
                  {advanceLimits ? new Price(advanceLimits.amount).round(2).toDisplayValue(currency) : "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

interface Props {
  readonly orderId: string;
}

const PriceByDepartmentDialog: FC<Props> = ({ orderId }) => {
  const [open, setOpen] = useState(false);
  const { data } = useOrderPriceByDepartment(orderId);

  if (!data) {
    return null;
  }

  return (
    <>
      <IconButton size="small" sx={{ ml: 0.5 }} aria-label="Ceny produktów wg działu" onClick={() => setOpen(true)}>
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <InfoOutlinedIcon color="primary" fontSize="small" />
          Ceny produktów według sprzedawcy
        </DialogTitle>
        <DialogContent>
          <PriceTable
            sellerAssignmentOrder={data.sellerAssignmentOrder}
            advance={data.advance}
            proforma={data.proforma}
            currency={data.currency as Currency}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Zamknij</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PriceByDepartmentDialog;
